import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import {
  TreasuryService,
  ITreasurySettings,
  type TFinancialAccount,
} from '@domains/treasury/data';
import { switchMap } from 'rxjs';

export interface TreasurySettingsState {
  treasurySettings: ITreasurySettings | null;
  physicalAccounts: TFinancialAccount[];
  bankAccounts: TFinancialAccount[];
  isLoading: boolean;
  isSaving: boolean;
  loadingPhysicalAccounts: boolean;
  loadingBankAccounts: boolean;
  error: string | null;
}

@Injectable({ providedIn: 'root' })
export class TreasurySettingsStore extends ComponentStore<TreasurySettingsState> {
  private readonly treasuryService = inject(TreasuryService);


  public readonly treasurySettings = toSignal(
    this.select((state) => state.treasurySettings),
    { initialValue: null },
  );
  public readonly physicalAccounts = toSignal(
    this.select((state) => state.physicalAccounts),
    { initialValue: [] as TFinancialAccount[] },
  );
  public readonly bankAccounts = toSignal(
    this.select((state) => state.bankAccounts),
    { initialValue: [] as TFinancialAccount[] },
  );
  public readonly isLoading = toSignal(this.select((state) => state.isLoading), {
    initialValue: false,
  });
  public readonly isSaving = toSignal(this.select((state) => state.isSaving), {
    initialValue: false,
  });
  public readonly loadingPhysicalAccounts = toSignal(
    this.select((state) => state.loadingPhysicalAccounts),
    { initialValue: false },
  );
  public readonly loadingBankAccounts = toSignal(
    this.select((state) => state.loadingBankAccounts),
    { initialValue: false },
  );
  public readonly error = toSignal(this.select((state) => state.error), {
    initialValue: null,
  });

  constructor() {
    super({
      treasurySettings: null,
      physicalAccounts: [],
      bankAccounts: [],
      isLoading: false,
      isSaving: false,
      loadingPhysicalAccounts: false,
      loadingBankAccounts: false,
      error: null,
    });
  }

  readonly load = this.effect<void>(
    switchMap(() => {
      this.patchState({ isLoading: true, error: null });

      return this.treasuryService.getTreasurySettings().pipe(
        tapResponse({
          next: (treasurySettings) =>
            this.patchState({
              treasurySettings,
              isLoading: false,
              error: null,
            }),
          error: (error: unknown) =>
            this.patchState({
              isLoading: false,
              error: this.getErrorMessage(error),
            }),
        }),
      );
    }),
  );

  readonly save = this.effect<ITreasurySettings>(
    switchMap((treasurySettings) => {
      this.patchState({ isSaving: true, error: null });

      return this.treasuryService.saveTreasurySettings(treasurySettings).pipe(
        tapResponse({
          next: (updated) =>
            this.patchState({
              treasurySettings: updated,
              isSaving: false,
              error: null,
            }),
          error: (error: unknown) =>
            this.patchState({
              isSaving: false,
              error: this.getErrorMessage(error),
            }),
        }),
      );
    }),
  );

  loadPhysicalAccounts(): void {
    this.patchState({ loadingPhysicalAccounts: true });

    this.treasuryService.getPhysicalFinancialAccounts().subscribe({
      next: (physicalAccounts) =>
        this.patchState({
          physicalAccounts,
          loadingPhysicalAccounts: false,
        }),
      error: (error: unknown) =>
        this.patchState({
          loadingPhysicalAccounts: false,
          error: this.getErrorMessage(error),
        }),
    });
  }

  loadBankAccounts(): void {
    this.patchState({ loadingBankAccounts: true });

    this.treasuryService.getBankFinancialAccounts().subscribe({
      next: (bankAccounts) =>
        this.patchState({
          bankAccounts,
          loadingBankAccounts: false,
        }),
      error: (error: unknown) =>
        this.patchState({
          loadingBankAccounts: false,
          error: this.getErrorMessage(error),
        }),
    });
  }

  resetPhysicalAccountsReqState(): void {
    this.patchState({ loadingPhysicalAccounts: false });
  }

  resetBankAccountsReqState(): void {
    this.patchState({ loadingBankAccounts: false });
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }

    return 'Unexpected error';
  }
}
